import type { ActionCableManagerAPI } from 'components/AI/ActionCableManager'
import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { setAwaitingResponse, setIsLoading } from 'slices/aiConversations'
import type { SegmentSaveData } from 'slices/aiConversations/segmentVisualizationTypes'
import type { Message } from 'slices/aiConversations/types'

export interface UseAIChatActionsReturn {
  sendMessage: (content: string) => void
  saveSegment: (segmentData: SegmentSaveData) => void
  setIsLoading: (loading: boolean) => void
  setAwaitingResponse: (awaiting: boolean) => void
  addMessage: (message: Message) => void
  setMessages: (messages: Message[]) => void
  resetChatId: () => void
}

export interface UseAIChatActionsProps {
  wsManager: React.MutableRefObject<ActionCableManagerAPI | null>
  addMessageWithOrdering: (message: Message) => void
  setMessagesWithSync: (messages: Message[]) => void
  resetChatId: () => void
}

/**
 * Hook for chat actions (send, save, state updates).
 *
 * Handles:
 * - Sending messages via WebSocket
 * - Saving segments via WebSocket
 * - Loading state updates
 * - Message management actions
 * - Chat ID reset functionality
 */
export const useAIChatActions = ({
  wsManager,
  addMessageWithOrdering,
  setMessagesWithSync,
  resetChatId,
}: UseAIChatActionsProps): UseAIChatActionsReturn => {
  const dispatch = useDispatch()

  const sendMessage = useCallback(
    (content: string) => {
      if (!wsManager.current) {
        return
      }

      dispatch(setAwaitingResponse(true))
      dispatch(setIsLoading(true))
      wsManager.current.sendMessage(content)
    },
    [wsManager, dispatch]
  )

  const saveSegment = useCallback(
    (segmentData: SegmentSaveData) => {
      // Check if WebSocket manager exists and is connected
      if (!wsManager.current || !wsManager.current.connected) {
        return
      }

      // Set loading state to show typing indicator
      dispatch(setIsLoading(true))
      wsManager.current.saveSegment(segmentData)
    },
    [wsManager, dispatch]
  )

  const setIsLoadingAction = useCallback(
    (loading: boolean) => dispatch(setIsLoading(loading)),
    [dispatch]
  )

  const setAwaitingResponseAction = useCallback(
    (awaiting: boolean) => dispatch(setAwaitingResponse(awaiting)),
    [dispatch]
  )

  const addMessage = useCallback(
    (message: Message) => {
      addMessageWithOrdering(message)
    },
    [addMessageWithOrdering]
  )

  const setMessages = useCallback(
    (messages: Message[]) => {
      setMessagesWithSync(messages)
    },
    [setMessagesWithSync]
  )

  return {
    sendMessage,
    saveSegment,
    setIsLoading: setIsLoadingAction,
    setAwaitingResponse: setAwaitingResponseAction,
    addMessage,
    setMessages,
    resetChatId,
  }
}
